import { IExportMenuItem, Percent } from '@amcharts/amcharts4/core';

import { GraphLink, GraphNode } from '../../../../../../generated/model/models';

// Icons
export interface IconsType {
  BRANCH: string;
  PERSON: string;
  COMPANY: string;
}

export const Icons = {
  BRANCH: 'M20 4H4v2h16V4zm1 10v-2l-1-5H4l-1 5v2h1v6h10v-6h4v6h2v-6h1zm-9 4H6v-4h6v4z',
  PERSON:
    'M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z',
  COMPANY:
    'M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zm-2-8h-2v2h2v-2zm0 4h-2v2h2v-2z',
  PHONE:
    'M20.01 15.38c-1.23 0-2.42-.2-3.53-.56-.35-.12-.74-.03-1.01.24l-1.57 1.97c-2.83-1.35-5.48-3.9-6.89-6.83l1.95-1.66c.27-.28.35-.67.24-1.02-.37-1.11-.56-2.3-.56-3.53 0-.54-.45-.99-.99-.99H4.19C3.65 3 3 3.24 3 3.99 3 13.28 10.73 21 20.01 21c.71 0 .99-.63.99-1.18v-3.45c0-.54-.45-.99-.99-.99z',
  EMAIL:
    'M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z',
  WEBSITE:
    'M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm-5 14H4v-4h11v4zm0-5H4V9h11v4zm5 5h-4V9h4v9z',
  ADDRESS:
    'M13 12h7v1.5h-7zm0-2.5h7V11h-7zm0 5h7V16h-7zM21 4H3c-1.1 0-2 .9-2 2v13c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 15h-9V6h9v13z',
} as Record<GraphNode.EntityTypeEnum, string>;

export enum NodesColorEnum {
  MAIN_COMPANY = '#006dc6',
  COMPANY = '#29435d',
  BRANCH = '#4b637a',
  PERSON = '#748698',
  PHONE = '#718496',
  EMAIL = '#718496',
  WEBSITE = '#718496',
  ADDRESS = '#718496',
  TOOLTIP_BG = '#a4b0bc',
}
// Link weight
export enum ConnectionsWeightEnum {
  Historical = 2,
  Weak = 2,
  Strong = 4,
}

export enum NodesRadiustEnum {
  MAIN_COMPANY = 6,
  COMPANY = 4,
  BRANCH = 3,
  PERSON = 2,
  PHONE = 1,
  EMAIL = 1,
  WEBSITE = 1,
  ADDRESS = 1,
}

export interface MappedGraphNode extends GraphNode {
  historicalType: string;
  managementType?: string[];
  ownershipType?: string;
  path: string;
  color: string;
  childCount: number;
  companyUid: string;
  allHistoricalTypes?: string[];
  textKeywords?: string[];
  x?: Percent;
  y?: Percent;
  fixed?: boolean;
  hidden?: boolean;
  countryLabel?: string;
}

export const GraphLinkTypeEnum = {
  OWNERSHIP: 'OWNERSHIP',
  BRANCH: 'BRANCH',
  MANAGEMENT: 'MANAGEMENT',
  HOLDING: 'HOLDING',
  EMAIL: 'EMAIL',
  PHONE: 'PHONE',
  ADDRESS: 'ADDRESS',
  WEBSITE: 'WEBSITE',
  WEBSITE_ADDRESS: 'WEBSITE_ADDRESS',
} as Record<GraphLink.LinkTypeEnum, GraphLink.LinkTypeEnum>;

export const GraphEntityTypeEnum = {
  BRANCH: 'BRANCH',
  COMPANY: 'COMPANY',
  PERSON: 'PERSON',
  EMAIL: 'EMAIL',
  PHONE: 'PHONE',
  ADDRESS: 'ADDRESS',
  WEBSITE: 'WEBSITE',
} as Record<GraphNode.EntityTypeEnum, GraphNode.EntityTypeEnum>;

export const ManagementTypeEnum = {
  BENEFICIAL_OWNER: 'BENEFICIAL_OWNER',
  SHAREHOLDER: 'SHAREHOLDER',
  ADMINISTRATOR: 'ADMINISTRATOR',
  AUDITOR: 'AUDITOR',
  MEMBER_OF_THE_BOARD: 'MEMBER_OF_THE_BOARD',
  BRANCH_MANAGER: 'BRANCH_MANAGER',
  EMPOWERED_TO_SIGN: 'EMPOWERED_TO_SIGN',
  FOUNDER: 'FOUNDER',
  LIABLE_PARTNER: 'LIABLE_PARTNER',
  GENERAL_AGENT: 'GENERAL_AGENT',
  LIQUIDATOR: 'LIQUIDATOR',
  MANAGEMENT: 'MANAGEMENT',
  OTHER: 'OTHER',
  MEMBER_OF_SUPERVISORY_BOARD: 'MEMBER_OF_SUPERVISORY_BOARD',
} as Record<GraphLink.ManagementTypeEnum, GraphLink.ManagementTypeEnum>;

export const GraphHistoricalTypeEnum = {
  CURRENT: 'CURRENT',
  HISTORICAL: 'HISTORICAL',
} as Record<GraphLink.HistoricalTypeEnum, GraphLink.HistoricalTypeEnum>;

export const GraphStrengthTypeEnum = {
  STRONG: 'STRONG',
  WEAK: 'WEAK',
} as Record<GraphLink.LinkStrengthEnum, GraphLink.LinkStrengthEnum>;

export const GraphLinkTextEnum = {
  CURRENT_OWNERSHIP: 'CURRENT_OWNERSHIP',
  HISTORICAL_OWNERSHIP: 'HISTORICAL_OWNERSHIP',
  CURRENT_BRANCH: 'CURRENT_BRANCH',
  HISTORICAL_BRANCH: 'HISTORICAL_BRANCH',
  CURRENT_MANAGEMENT: 'CURRENT_MANAGEMENT',
  HISTORICAL_MANAGEMENT: 'HISTORICAL_MANAGEMENT',
  CURRENT_HOLDING: 'CURRENT_HOLDING',
  HISTORICAL_HOLDING: 'HISTORICAL_HOLDING',
  CURRENT_EMAIL: 'CURRENT_EMAIL',
  HISTORICAL_EMAIL: 'HISTORICAL_EMAIL',
  CURRENT_PHONE: 'CURRENT_PHONE',
  HISTORICAL_PHONE: 'HISTORICAL_PHONE',
  CURRENT_ADDRESS: 'CURRENT_ADDRESS',
  HISTORICAL_ADDRESS: 'HISTORICAL_ADDRESS',
  CURRENT_WEBSITE: 'CURRENT_WEBSITE',
  HISTORICAL_WEBSITE: 'HISTORICAL_WEBSITE',
  CURRENT_WEBSITE_ADDRESS: 'CURRENT_WEBSITE_ADDRESS',
  HISTORICAL_WEBSITE_ADDRESS: 'HISTORICAL_WEBSITE_ADDRESS',
};

export const WeakLinks = [
  GraphLinkTypeEnum.PHONE,
  GraphLinkTypeEnum.EMAIL,
  GraphLinkTypeEnum.WEBSITE,
  GraphLinkTypeEnum.ADDRESS,
];

export const LINK_STROKE_DASH = ['10,4'];

export const exportMenu: IExportMenuItem = {
  label: '...',
  menu: [
    {
      label: 'Image',
      menu: [
        { type: 'jpg', label: 'JPG' },
        { type: 'pdf', label: 'PDF' },
      ],
    },
    {
      label: 'Data',
      menu: [
        { type: 'json', label: 'JSON' },
        { type: 'csv', label: 'CSV' },
        { type: 'xlsx', label: 'XLSX' },
      ],
    },
  ],
};

export interface VisibilityTypes {
  hidden: string[];
  visible: string[];
}
